import { useMemo, useCallback, useState } from 'react';
import { Checkbox, Button } from '@carbon/react';
import { ChevronDown, ChevronRight } from '@carbon/react/icons';
import style from './v2-sidebar.module.scss';

/** Interest type hierarchy — parent groups with their subtypes and display labels */
export const INTEREST_TYPES = {
  mineral: {
    label: 'Mineral',
    subtypes: [
      { id: 'executive', label: 'Executive' },
      { id: 'royalty', label: 'Royalty' },
      { id: 'bonus', label: 'Bonus' },
      { id: 'delay', label: 'Delay' },
      { id: 'working', label: 'Working' },
      { id: 'orri', label: 'ORRI' },
    ],
  },
  surface: { label: 'Surface', subtypes: [] },
} as const;

const MINERAL_CHILDREN = INTEREST_TYPES.mineral.subtypes.map((c) => c.id);

/** Map from child interest type → parent interest type */
export const INTEREST_TYPE_PARENT: Record<string, string> = Object.fromEntries(
  MINERAL_CHILDREN.map((child) => [child, 'mineral'])
);

interface InterestTypeLatticeFilterProps {
  selected: string[];
  onChange: (next: string[]) => void;
}

export function InterestTypeLatticeFilter({
  selected,
  onChange,
}: InterestTypeLatticeFilterProps) {
  const [mineralExpanded, setMineralExpanded] = useState(false);
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const mineralChildrenSelected = useMemo(
    () => MINERAL_CHILDREN.filter((c) => selectedSet.has(c)),
    [selectedSet]
  );
  const allMineralSelected =
    mineralChildrenSelected.length === MINERAL_CHILDREN.length;
  const someMineralSelected =
    mineralChildrenSelected.length > 0 && !allMineralSelected;

  const handleMineralParentChange = useCallback(() => {
    if (allMineralSelected) {
      onChange(
        selected.filter(
          (v) => !(MINERAL_CHILDREN as readonly string[]).includes(v)
        )
      );
    } else {
      const withoutMineral = selected.filter(
        (v) => !(MINERAL_CHILDREN as readonly string[]).includes(v)
      );
      onChange([...withoutMineral, ...MINERAL_CHILDREN]);
    }
  }, [allMineralSelected, selected, onChange]);

  const handleToggle = useCallback(
    (id: string) => {
      if (selectedSet.has(id)) {
        onChange(selected.filter((v) => v !== id));
      } else {
        onChange([...selected, id]);
      }
    },
    [selected, selectedSet, onChange]
  );

  const tooltipText = mineralExpanded
    ? 'Hide mineral subtypes'
    : `Show ${MINERAL_CHILDREN.length} mineral subtypes`;

  return (
    <div className={style.nestedCheckboxGroup}>
      {/* Mineral — parent row: caret + checkbox */}
      <div className={style.parentCheckboxRow}>
        <Button
          hasIconOnly
          renderIcon={mineralExpanded ? ChevronDown : ChevronRight}
          iconDescription={tooltipText}
          tooltipPosition="right"
          kind="ghost"
          size="sm"
          className={style.nestingCaret}
          onClick={() => setMineralExpanded((prev) => !prev)}
        />
        <Checkbox
          id="interest-type-mineral-parent"
          labelText="Mineral"
          checked={allMineralSelected}
          indeterminate={someMineralSelected}
          onChange={handleMineralParentChange}
        />
      </div>
      {/* Mineral subtypes — indented, collapsible */}
      {mineralExpanded && (
        <div className={style.nestedChildren}>
          {INTEREST_TYPES.mineral.subtypes.map((child) => (
            <Checkbox
              key={child.id}
              id={`interest-type-${child.id}`}
              labelText={child.label}
              checked={selectedSet.has(child.id)}
              onChange={() => handleToggle(child.id)}
            />
          ))}
        </div>
      )}
      {/* Surface — standalone, spacer keeps checkbox aligned with Mineral */}
      <div className={style.parentCheckboxRow}>
        <span className={style.nestingCaretSpacer} />
        <Checkbox
          id="interest-type-surface"
          labelText="Surface"
          checked={selectedSet.has('surface')}
          onChange={() => handleToggle('surface')}
        />
      </div>
    </div>
  );
}
